import { Drawer } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { Spinner } from 'components';
import { useAuth, useCreateDraft, useWindowSize } from 'hooks';
import { Links } from 'settings';
import { theme } from 'styles';
import { Achievement, User } from 'types';

import { AchievementCard } from '../AchievementCard';
import { GoalEmptyState } from '../GoalEmptyState';
import { GoalForm } from '../GoalForm';
import { GoalsList } from '../GoalsList';
import { SectionW, EmptyStateW, Title, AddButton, AccomplishedLabel } from './styled';

const drawerPaperSx = {
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  height: '92vh',
  overflowX: 'hidden' as const,
  overflowY: 'auto' as const,
};

interface AchievementSectionProps
  extends Pick<Achievement, 'id' | 'title' | 'description' | 'color' | 'progress' | 'goals'> {
  employeeId: string;
  ownerId: string;
  isFullView: boolean;
  usersList: User[];
  isProfilePreview?: boolean;
}

export function AchievementSection({
  id,
  title,
  description,
  color,
  progress,
  goals = [],
  employeeId,
  usersList,
  isFullView,
  isProfilePreview,
  ownerId,
}: AchievementSectionProps): ReactElement {
  const isGoalsList = !!goals?.length;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser, isManager } = useAuth();
  const { isMobile } = useWindowSize();
  const currentUserId = currentUser?.id ?? '';

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const { draft, isLoading: isDraftLoading } = useCreateDraft(isMobile && isBottomSheetOpen);

  const disableAddGoal =
    (!isManager && currentUserId !== ownerId) ||
    (isManager && employeeId === currentUserId && ownerId !== currentUserId);

  const addGoalLink = generatePath(
    Links.profile.achievements.achievement.achievementId.createGoal,
    {
      achievementId: id,
      employeeId,
    },
  );

  const achievementDetailsLink = generatePath(
    Links.profile.achievements.achievement.achievementId.index,
    {
      achievementId: id,
      employeeId,
    },
  );

  const handleAddGoal = () => {
    if (disableAddGoal) return;
    navigate(addGoalLink);
  };

  return (
    <SectionW isFullView={isFullView}>
      <AchievementCard
        key={id}
        title={title}
        color={color}
        description={description}
        progress={progress}
        onClick={() => {
          navigate(achievementDetailsLink);
        }}
      />
      {isGoalsList ? (
        <>
          {progress === 100 ? (
            <AccomplishedLabel>
              {t('career.achievements.accomplishedAchievement')}
            </AccomplishedLabel>
          ) : (
            <AddButton disabled={disableAddGoal} onClick={handleAddGoal}>
              <AddIcon
                style={{
                  color: '#fff',
                  border: `1px solid ${theme.palette.info.main}`,
                  borderRadius: '50%',
                }}
              />
              <Title>{t('career.achievements.addGoal')}</Title>
            </AddButton>
          )}
          <GoalsList
            data={goals}
            isProfilePreview={isProfilePreview}
            achievementId={id}
            employeeId={employeeId}
            usersList={usersList}
          />
        </>
      ) : (
        <EmptyStateW>
          <GoalEmptyState
            addGoalLink={addGoalLink}
            onAddGoal={isMobile ? handleAddGoal : undefined}
          />
        </EmptyStateW>
      )}

      {isMobile && (
        <Drawer
          anchor="bottom"
          open={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          PaperProps={{ sx: drawerPaperSx }}
        >
          {isDraftLoading || !draft ? (
            <Spinner />
          ) : (
            <GoalForm
              isEdit={false}
              draft={draft}
              usersList={usersList}
              achievementId={id}
              employeeId={employeeId}
              onClose={() => setIsBottomSheetOpen(false)}
            />
          )}
        </Drawer>
      )}
    </SectionW>
  );
}
